import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Edit2, Trash2, Package, CheckCircle, AlertCircle, Wrench, X } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { ModulePageTabs, ModuleTabContent, ChildTabPlaceholder } from './moduleTabs';
import LocalizedDateInput from './LocalizedDateInput';
import TableControls from './TableControls';
import { api } from './api';
import { StandardActionsCell, StandardCreateButton } from './StandardUI';
import { isLegacyBuCode, translateDas } from './strategicMapping';
import ProductionGlossary from './ProductionGlossary';
import FunctionResourcesOverview from './FunctionResourcesOverview';
import { FunctionArchitectureOverview, FunctionProcessOverview } from './FunctionStructuralViews';
import FunctionAssistant from './FunctionAssistant';
import ProductionServices from './ProductionServices';
import ProductionOverview from './ProductionOverview';
import ProductionManufacturingKit from './ProductionManufacturingKit';
import ProductionTools from './ProductionTools';
import {
  buildTeamAgentDirectory,
  findAgentTeam,
  getDirectoryAgentLabel,
  normalizeTeamCode,
  resolveDirectoryAgent,
  TEAM_CODES
} from './teamDirectory';

const Production = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Translations
  const translations = {
    FR: {
      title: 'Production & Logistique',
      subtitle: 'Commandes, Fournisseurs et Gestion des Stocks',
      overview: 'Vue d\'ensemble',
      commandes: 'Commandes',
      fournisseurs: 'Fournisseurs',
      stocks: 'Stocks',
      glossary: 'Glossaire',
      totalCommandes: 'Total Commandes',
      commandesLivrees: 'Commandes Livrées',
      totalFournisseurs: 'Fournisseurs',
      alertStocks: 'Stocks Bas',
      commandesParMois: 'Commandes par Mois',
      stocksParProduit: 'Stocks par Produit',
      numero: 'Numéro',
      client: 'Client',
      produit: 'Produit',
      quantite: 'Quantité',
      statut: 'Statut',
      date: 'Date',
      actions: 'Actions',
      seuil: 'Seuil',
      unite: 'Unité',
      categorie: 'Catégorie',
      pays: 'Pays',
      email: 'Email',
      telephone: 'Téléphone',
      nom: 'Nom',
      nouvelleCommande: 'Nouvelle Commande',
      nouveauFournisseur: 'Nouveau Fournisseur',
      ajouterStock: 'Ajouter Stock',
      nomFournisseur: 'Nom du fournisseur',
      source: 'Source',
      sources: 'Sources',
      lignesDepenses: 'Lignes dépenses',
      lignesStocks: 'Lignes stocks',
      montantChf: 'Montant CHF',
      montantCfa: 'Montant CFA',
      derniereOperation: 'Dernière opération',
      departement: 'Département',
      team: 'Team',
      agent: 'Agent',
      reference: 'Réf.',
      nbReferences: 'Nb références',
      ficheFournisseur: 'Fiche fournisseur',
      registreFournisseurs: 'Registre fournisseurs consolidé depuis Dépenses et Stocks & Actifs.',
      preparationFournisseur: 'Préparer fournisseur',
      aucuneDonnee: 'Aucune donnée fournisseur disponible.',
      voir: 'Voir',
      teamZh: 'TZH - Team Zurich',
      teamSn: 'TSN - Team Sénégal',
      teamZhCollective: 'Team ZH (collectif)',
      teamSnCollective: 'Team SN (collectif)',
      modifierFournisseur: 'Modifier le fournisseur',
      champsModifiables: 'Champs modifiables',
      donneesSourceLectureSeule: 'Données sources en lecture seule',
      confirmerSuppressionFournisseur: 'Supprimer ce fournisseur du registre local ?',
      agentNonAttribue: 'Non attribué',
      agentSourceLoading: 'Chargement de l’annuaire sécurisé RH-001…',
      agentSourceAvailable: 'Personnes proposées depuis l’annuaire sécurisé RH-001.',
      agentSourceUnavailable: 'Annuaire RH-001 indisponible : seuls les collectifs des équipes sont proposés.',
      nonRenseigne: 'Non renseigné',
      agentHistorique: 'valeur historique',
      affectationAQualifier: 'À qualifier',
      coherenceAffectation: 'Cohérence Team / Agent',
      affectationCoherente: 'Cohérente',
      affectationIncoherente: 'Source incohérente : affectation à qualifier',
      ok: 'OK',
      bas: 'BAS',
      creer: 'Créer',
      modifier: 'Modifier',
      supprimer: 'Supprimer',
      annuler: 'Annuler',
      remplirChamps: 'Veuillez remplir les champs obligatoires',
      pilotTitle: 'Pilote local Production',
      pilotScope: 'Lecture seule',
      pilotDescription: 'Commandes et stocks sont des données locales de démonstration. Elles ne constituent pas des données consolidées.',
      pilotSource: 'Source : pilote local M3S',
      pilotFreshness: 'Fraîcheur : scénario de travail',
      connectedSuppliers: 'L’onglet Fournisseurs reste un registre connecté séparé, hors de ce pilote.',
      noWriteAction: 'Les ajouts, modifications et suppressions seront réintroduits plus tard avec droits, confirmations et traçabilité.',
      manufacturingTitle: 'Fabrication',
      manufacturingStatus: 'Premier cas numérique',
      manufacturingDescription: 'Le kit documentaire existant illustre une fabrication numérique. Aucun registre d’ordres ni total consolidé de fabrication n’est encore raccordé.',
      manufacturingGuardrail: 'Cette vue prépare le cadre métier sans créer de faux ordres de fabrication.',
      manufacturingSteps: ['Besoin et livrable', 'Étapes et moyens', 'Responsable et rattachements', 'Contrôle, version et remise'],
      manufacturingNext: 'À qualifier avant tout ordre : objet à produire, demande autorisée, étapes, moyens, responsable, contrôle qualité et version livrable. Les kits DOC/RH restent dans leurs cycles existants, sans second enregistrement.',
      manufacturingSource: 'Cadre de référence : Production V2 de travail, sections 2, 3 et 10. Aucun ordre, délai ou coût réel n’est déduit de ce cadrage.',
      openServices: 'Ouvrir le suivi des prestations'
    },
    EN: {
      title: 'Production & Logistics',
      subtitle: 'Orders, Suppliers and Stock Management',
      overview: 'Overview',
      commandes: 'Orders',
      fournisseurs: 'Suppliers',
      stocks: 'Stock',
      glossary: 'Glossary',
      totalCommandes: 'Total Orders',
      commandesLivrees: 'Delivered Orders',
      totalFournisseurs: 'Suppliers',
      alertStocks: 'Low Stock',
      commandesParMois: 'Orders by Month',
      stocksParProduit: 'Stock by Product',
      numero: 'Number',
      client: 'Client',
      produit: 'Product',
      quantite: 'Quantity',
      statut: 'Status',
      date: 'Date',
      actions: 'Actions',
      seuil: 'Threshold',
      unite: 'Unit',
      categorie: 'Category',
      pays: 'Country',
      email: 'Email',
      telephone: 'Phone',
      nom: 'Name',
      nouvelleCommande: 'New Order',
      nouveauFournisseur: 'New Supplier',
      ajouterStock: 'Add Stock',
      nomFournisseur: 'Supplier name',
      source: 'Source',
      sources: 'Sources',
      lignesDepenses: 'Expense lines',
      lignesStocks: 'Stock lines',
      montantChf: 'Amount CHF',
      montantCfa: 'Amount CFA',
      derniereOperation: 'Last operation',
      departement: 'Department',
      team: 'Team',
      agent: 'Agent',
      reference: 'Ref.',
      nbReferences: 'Ref. count',
      ficheFournisseur: 'Supplier record',
      registreFournisseurs: 'Supplier register consolidated from Expenses and Stock & Assets.',
      preparationFournisseur: 'Prepare supplier',
      aucuneDonnee: 'No supplier data available.',
      voir: 'View',
      teamZh: 'TZH - Zurich Team',
      teamSn: 'TSN - Senegal Team',
      teamZhCollective: 'Team ZH (collective)',
      teamSnCollective: 'Team SN (collective)',
      modifierFournisseur: 'Edit Supplier',
      champsModifiables: 'Editable fields',
      donneesSourceLectureSeule: 'Read-only source data',
      confirmerSuppressionFournisseur: 'Delete this supplier from the local register?',
      agentNonAttribue: 'Unassigned',
      agentSourceLoading: 'Loading the secure RH-001 directory…',
      agentSourceAvailable: 'People are suggested from the secure RH-001 directory.',
      agentSourceUnavailable: 'RH-001 directory unavailable: only team collectives are offered.',
      nonRenseigne: 'Not specified',
      agentHistorique: 'historical value',
      affectationAQualifier: 'To be qualified',
      coherenceAffectation: 'Team / Agent consistency',
      affectationCoherente: 'Consistent',
      affectationIncoherente: 'Inconsistent source: assignment to be qualified',
      ok: 'OK',
      bas: 'LOW',
      creer: 'Create',
      modifier: 'Edit',
      supprimer: 'Delete',
      annuler: 'Cancel',
      remplirChamps: 'Please fill in all required fields',
      pilotTitle: 'Local Production pilot',
      pilotScope: 'Read only',
      pilotDescription: 'Orders and stock are local demonstration data. They are not consolidated data.',
      pilotSource: 'Source: M3S local pilot',
      pilotFreshness: 'Freshness: working scenario',
      connectedSuppliers: 'The Suppliers tab remains a separate connected register outside this pilot.',
      noWriteAction: 'Create, edit and delete actions will return later with permissions, confirmations and traceability.',
      manufacturingTitle: 'Manufacturing',
      manufacturingStatus: 'First digital case',
      manufacturingDescription: 'The existing document kit illustrates digital production. No manufacturing order register or consolidated manufacturing total is connected yet.',
      manufacturingGuardrail: 'This view prepares the business scope without creating fake manufacturing orders.',
      manufacturingSteps: ['Need and deliverable', 'Steps and resources', 'Owner and links', 'Quality, version and delivery'],
      manufacturingNext: 'Qualify before any order: item to produce, authorised request, steps, resources, owner, quality control and deliverable version. DOC/HR kits remain in their existing cycles, without a second record.',
      manufacturingSource: 'Reference framework: Production V2 working version, sections 2, 3 and 10. No real order, deadline or cost is inferred from this scope.',
      openServices: 'Open service delivery follow-up'
    },
    DE: {
      title: 'Produktion & Logistik',
      subtitle: 'Bestellungen, Lieferanten und Lagerverwaltung',
      overview: 'Übersicht',
      commandes: 'Bestellungen',
      fournisseurs: 'Lieferanten',
      stocks: 'Lagerbestand',
      glossary: 'Glossar',
      totalCommandes: 'Gesamtbestellungen',
      commandesLivrees: 'Gelieferte Bestellungen',
      totalFournisseurs: 'Lieferanten',
      alertStocks: 'Niedriger Bestand',
      commandesParMois: 'Bestellungen pro Monat',
      stocksParProduit: 'Lagerbestand nach Produkt',
      numero: 'Nummer',
      client: 'Kunde',
      produit: 'Produkt',
      quantite: 'Menge',
      statut: 'Status',
      date: 'Datum',
      actions: 'Aktionen',
      seuil: 'Schwellenwert',
      unite: 'Einheit',
      categorie: 'Kategorie',
      pays: 'Land',
      email: 'E-Mail',
      telephone: 'Telefon',
      nom: 'Name',
      nouvelleCommande: 'Neue Bestellung',
      nouveauFournisseur: 'Neuer Lieferant',
      ajouterStock: 'Bestand hinzufügen',
      nomFournisseur: 'Lieferantenname',
      source: 'Quelle',
      sources: 'Quellen',
      lignesDepenses: 'Ausgabenzeilen',
      lignesStocks: 'Bestandszeilen',
      montantChf: 'Betrag CHF',
      montantCfa: 'Betrag CFA',
      derniereOperation: 'Letzte Buchung',
      departement: 'Abteilung',
      team: 'Team',
      agent: 'Agent',
      reference: 'Ref.',
      nbReferences: 'Anzahl Ref.',
      ficheFournisseur: 'Lieferantendossier',
      registreFournisseurs: 'Lieferantenregister konsolidiert aus Ausgaben und Bestand & Aktiven.',
      preparationFournisseur: 'Lieferant vorbereiten',
      aucuneDonnee: 'Keine Lieferantendaten verfügbar.',
      voir: 'Ansehen',
      teamZh: 'TZH - Team Zürich',
      teamSn: 'TSN - Team Senegal',
      teamZhCollective: 'Team ZH (gemeinsam)',
      teamSnCollective: 'Team SN (gemeinsam)',
      modifierFournisseur: 'Lieferant bearbeiten',
      champsModifiables: 'Bearbeitbare Felder',
      donneesSourceLectureSeule: 'Quelldaten nur zum Lesen',
      confirmerSuppressionFournisseur: 'Diesen Lieferanten aus dem lokalen Register löschen?',
      agentNonAttribue: 'Nicht zugewiesen',
      agentSourceLoading: 'Geschütztes Verzeichnis RH-001 wird geladen…',
      agentSourceAvailable: 'Personen werden aus dem geschützten Verzeichnis RH-001 vorgeschlagen.',
      agentSourceUnavailable: 'Verzeichnis RH-001 nicht verfügbar: nur Team-Kollektive werden angeboten.',
      nonRenseigne: 'Nicht angegeben',
      agentHistorique: 'historischer Wert',
      affectationAQualifier: 'Zu klären',
      coherenceAffectation: 'Konsistenz Team / Agent',
      affectationCoherente: 'Konsistent',
      affectationIncoherente: 'Inkonsistente Quelle: Zuordnung ist zu klären',
      ok: 'OK',
      bas: 'NIEDRIG',
      creer: 'Erstellen',
      modifier: 'Bearbeiten',
      supprimer: 'Löschen',
      annuler: 'Abbrechen',
      remplirChamps: 'Bitte füllen Sie alle erforderlichen Felder aus',
      pilotTitle: 'Lokaler Produktionspilot',
      pilotScope: 'Nur lesen',
      pilotDescription: 'Bestellungen und Bestände sind lokale Demonstrationsdaten. Sie sind keine konsolidierten Daten.',
      pilotSource: 'Quelle: lokaler M3S-Pilot',
      pilotFreshness: 'Aktualität: Arbeitsszenario',
      connectedSuppliers: 'Der Reiter Lieferanten bleibt ein separates verbundenes Register außerhalb dieses Piloten.',
      noWriteAction: 'Erstellen, Bearbeiten und Löschen werden später mit Rechten, Bestätigungen und Nachverfolgung wieder eingeführt.',
      manufacturingTitle: 'Herstellung',
      manufacturingStatus: 'Erster digitaler Fall',
      manufacturingDescription: 'Das bestehende Dokumenten-Kit veranschaulicht digitale Herstellung. Ein Fertigungsauftragsregister oder konsolidierte Herstellungszahlen sind noch nicht angebunden.',
      manufacturingGuardrail: 'Diese Ansicht bereitet den fachlichen Rahmen vor, ohne fiktive Fertigungsaufträge anzulegen.',
      manufacturingSteps: ['Bedarf und Ergebnis', 'Schritte und Mittel', 'Verantwortung und Verknüpfungen', 'Qualität, Version und Übergabe'],
      manufacturingNext: 'Vor jedem Auftrag klären: herzustellendes Objekt, genehmigter Bedarf, Schritte, Mittel, Verantwortung, Qualitätskontrolle und Ergebnisversion. DOC/HR-Kits bleiben in ihren bestehenden Zyklen, ohne zweiten Eintrag.',
      manufacturingSource: 'Referenzrahmen: Production V2 Arbeitsfassung, Abschnitte 2, 3 und 10. Daraus werden keine realen Aufträge, Fristen oder Kosten abgeleitet.',
      openServices: 'Leistungsverfolgung öffnen'
    }
  };

  const t = translations[language];

  // Data translations
  const dataTranslations = {
    // Status values
    statuses: {
      FR: { 'Livrée': 'Livrée', 'En cours': 'En cours', 'Préparation': 'Préparation' },
      EN: { 'Livrée': 'Delivered', 'En cours': 'In Progress', 'Préparation': 'Preparation' },
      DE: { 'Livrée': 'Geliefert', 'En cours': 'Laufend', 'Préparation': 'Vorbereitung' }
    },
    // Products
    products: {
      FR: {
        'Licences IT': 'Licences IT',
        'Matériel Informatique': 'Matériel Informatique',
        'Fournitures Bureau': 'Fournitures Bureau',
        'Consommables': 'Consommables',
        'Solutions IT': 'Solutions IT',
        'Formation': 'Formation',
        'Consultation': 'Consultation',
        'Implémentation': 'Implémentation'
      },
      EN: {
        'Licences IT': 'IT Licenses',
        'Matériel Informatique': 'IT Equipment',
        'Fournitures Bureau': 'Office Supplies',
        'Consommables': 'Consumables',
        'Solutions IT': 'IT Solutions',
        'Formation': 'Training',
        'Consultation': 'Consulting',
        'Implémentation': 'Implementation'
      },
      DE: {
        'Licences IT': 'IT-Lizenzen',
        'Matériel Informatique': 'IT-Ausrüstung',
        'Fournitures Bureau': 'Bürobedarf',
        'Consommables': 'Verbrauchsmaterial',
        'Solutions IT': 'IT-Lösungen',
        'Formation': 'Schulung',
        'Consultation': 'Beratung',
        'Implémentation': 'Implementierung'
      }
    },
    // Months
    months: {
      FR: { 'Fév': 'Fév', 'Mar': 'Mar', 'Avr': 'Avr' },
      EN: { 'Fév': 'Feb', 'Mar': 'Mar', 'Apr': 'Apr' },
      DE: { 'Fév': 'Feb', 'Mar': 'Mär', 'Avr': 'Apr' }
    },
    // Categories
    categories: {
      FR: {
        'Matériel': 'Matériel',
        'Logiciels': 'Logiciels',
        'Services': 'Services',
        'Bien immobilier': 'Bien immobilier',
        'Immobilier': 'Immobilier',
        'Don matériel': 'Don matériel',
        'Aide Sociale': 'Aide sociale',
        'Frais Administratifs': 'Frais administratifs',
        'Chantier': 'Chantier',
        'Gros Œuvres': 'Gros œuvres'
      },
      EN: {
        'Matériel': 'Hardware',
        'Logiciels': 'Software',
        'Services': 'Services',
        'Bien immobilier': 'Real estate asset',
        'Immobilier': 'Real estate',
        'Don matériel': 'In-kind donation',
        'Aide Sociale': 'Social aid',
        'Frais Administratifs': 'Administrative fees',
        'Chantier': 'Construction site',
        'Gros Œuvres': 'Structural works'
      },
      DE: {
        'Matériel': 'Hardware',
        'Logiciels': 'Software',
        'Services': 'Dienstleistungen',
        'Bien immobilier': 'Immobilienwert',
        'Immobilier': 'Immobilien',
        'Don matériel': 'Sachspende',
        'Aide Sociale': 'Sozialhilfe',
        'Frais Administratifs': 'Verwaltungskosten',
        'Chantier': 'Baustelle',
        'Gros Œuvres': 'Rohbauarbeiten'
      }
    },
    sources: {
      FR: { 'Dépenses': 'Dépenses', 'Stocks & Actifs': 'Stocks & Actifs' },
      EN: { 'Dépenses': 'Expenses', 'Stocks & Actifs': 'Stock & Assets' },
      DE: { 'Dépenses': 'Ausgaben', 'Stocks & Actifs': 'Bestand & Aktiven' }
    },
    departments: {
      FR: {
        ADMIN_ORG: 'Gouvernance & Organisation',
        IMPORT_EXPORT: 'Business',
        SOCIAL: 'Social',
        IMMO: 'Business',
        TECH_DIGITAL: 'Digital',
        'Finances': 'Finances',
        'Production': 'Production',
        'Stock & Actifs': 'Stocks & Actifs',
        'Stocks & Actifs': 'Stocks & Actifs'
      },
      EN: {
        ADMIN_ORG: 'Governance & Organization',
        IMPORT_EXPORT: 'Business',
        SOCIAL: 'Social',
        IMMO: 'Business',
        TECH_DIGITAL: 'Digital',
        'Finances': 'Finance',
        'Production': 'Production',
        'Stock & Actifs': 'Stock & Assets',
        'Stocks & Actifs': 'Stock & Assets'
      },
      DE: {
        ADMIN_ORG: 'Governance & Organisation',
        IMPORT_EXPORT: 'Business',
        SOCIAL: 'Soziales',
        IMMO: 'Business',
        TECH_DIGITAL: 'Digital',
        'Finances': 'Finanzen',
        'Production': 'Produktion',
        'Stock & Actifs': 'Bestand & Aktiven',
        'Stocks & Actifs': 'Bestand & Aktiven'
      }
    },
    countries: {
      FR: { 'Sénégal': 'Sénégal', 'France': 'France', 'Suisse': 'Suisse' },
      EN: { 'Sénégal': 'Senegal', 'France': 'France', 'Suisse': 'Switzerland' },
      DE: { 'Sénégal': 'Senegal', 'France': 'Frankreich', 'Suisse': 'Schweiz' }
    },
    // Chart bar names
    chartLabels: {
      FR: { 'quantite': 'Quantité', 'seuil': 'Seuil' },
      EN: { 'quantite': 'Quantity', 'seuil': 'Threshold' },
      DE: { 'quantite': 'Menge', 'seuil': 'Schwellenwert' }
    }
  };

  // Helper function to translate data values
  const readScalar = (value) => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toISOString();
    if (typeof value !== 'object') return String(value).trim();
    const nested = value.value ?? value.date ?? value.timestamp ?? value.stringValue ?? value.string_value;
    return nested === undefined ? '' : readScalar(nested);
  };
  const normalizeLookupKey = (value) => readScalar(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  const statusKeys = {
    'LIVREE': 'Livrée',
    'DELIVERED': 'Livrée',
    'EN COURS': 'En cours',
    'IN PROGRESS': 'En cours',
    'LAUFEND': 'En cours',
    'PREPARATION': 'Préparation',
    'VORBEREITUNG': 'Préparation'
  };
  const countryKeys = {
    'SN': 'Sénégal',
    'SENEGAL': 'Sénégal',
    'CH': 'Suisse',
    'FRANCE': 'France',
    'SUISSE': 'Suisse',
    'SWITZERLAND': 'Suisse',
    'SCHWEIZ': 'Suisse'
  };
  const normalizeStatus = (status) => statusKeys[normalizeLookupKey(status)] || status;
  const translateStatus = (status) => {
    const normalized = normalizeStatus(status);
    return dataTranslations.statuses[language]?.[normalized] || status;
  };
  const getStatusClass = (status) => {
    const normalized = normalizeStatus(status);
    if (normalized === 'Livrée') return 'bg-green-900 text-green-200';
    if (normalized === 'Préparation') return 'bg-amber-900 text-amber-200';
    return 'bg-blue-900 text-blue-200';
  };
  const translateProduct = (product) => dataTranslations.products[language]?.[product] || product;
  const normalizedLabelKey = (value) => normalizeLookupKey(value).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  const translateFromDictionary = (dictionary, value) => {
    if (!value || value === '-') return '-';
    const exact = dictionary[language]?.[value] || dictionary.FR?.[value];
    if (exact) return exact;
    const normalized = normalizedLabelKey(value);
    const entries = Object.entries(dictionary[language] || {});
    const found = entries.find(([key]) => normalizedLabelKey(key) === normalized);
    if (found) return found[1];
    const fallback = Object.entries(dictionary.FR || {}).find(([key]) => normalizedLabelKey(key) === normalized);
    return fallback?.[1] || String(value).replace(/[_-]+/g, ' ');
  };
  const translateCategory = (category) => translateFromDictionary(dataTranslations.categories, category);
  const translateSource = (source) => translateFromDictionary(dataTranslations.sources, source);
  const translateDepartment = (department) => (
    isLegacyBuCode(department) ? translateDas(department, language) : translateFromDictionary(dataTranslations.departments, department)
  );
  const translateJoinedValues = (value, translator) => String(value || '-')
    .split(',')
    .map(item => translator(item.trim()))
    .join(', ');
  const normalizeCountry = (country) => countryKeys[normalizeLookupKey(country)] || country;
  const translateCountry = (country) => {
    const normalized = normalizeCountry(country);
    return dataTranslations.countries[language]?.[normalized] || country;
  };
  const teamOptions = [
    { value: 'Team_ZH', label: t.teamZh },
    { value: 'Team_SN', label: t.teamSn }
  ];
  const normalizeTeam = (team) => normalizeTeamCode(readScalar(team)) || '-';
  const translateTeam = (team) => {
    const normalized = normalizeTeam(team);
    return teamOptions.find(option => option.value === normalized)?.label || normalized;
  };
  const resolveEditableTeam = (team) => {
    const candidates = String(team || '')
      .split(',')
      .map(value => normalizeTeam(value.trim()));
    return candidates.find(candidate => teamOptions.some(option => option.value === candidate)) || 'Team_ZH';
  };
  const resolveEditableAgent = (team, agent) => {
    return resolveDirectoryAgent(team, agent, agentsByTeam);
  };
  const normalizeAgent = (agent) => readScalar(agent);
  const getAgentTeam = (agent, directory = agentsByTeam) => findAgentTeam(agent, directory);
  const splitSourceValues = (value) => readScalar(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  const translateAgent = (agent) => {
    return getDirectoryAgentLabel(agent, agentsByTeam);
  };
  const supplierAgentLabel = (supplier) => {
    const agents = supplier?.agent && supplier.agent !== '-'
      ? translateJoinedValues(supplier.agent, translateAgent)
      : '';
    if (!supplier?.assignmentIssueCount) return agents || '-';
    return agents ? `${agents} · ${t.affectationAQualifier}` : t.affectationAQualifier;
  };
  const supplierCategoryOptions = ['Matériel', 'Logiciels', 'Services', 'Bien immobilier', 'Frais Administratifs', 'Chantier'];
  const supplierDepartmentOptions = Object.entries(dataTranslations.departments.FR)
    .filter(([, label], index, entries) => entries.findIndex(([, candidate]) => candidate === label) === index)
    .map(([value]) => value);
  const getDefaultFormData = (type = 'commande') => ({
    numero: '',
    client: '',
    produit: '',
    quantite: '',
    nom: '',
    email: '',
    telephone: '',
    categorie: 'Services',
    departement: '',
    pays: 'Sénégal',
    team: 'Team_ZH',
    agent: '',
    seuil: '',
    unite: 'Unité',
    statut: 'En cours',
    date: new Date().toISOString().split('T')[0],
    ...(type === 'stock' ? { produit: 'Licences IT', quantite: '', seuil: '', unite: 'Unité' } : {}),
    ...(type === 'fournisseur' ? { categorie: 'Services', departement: '', pays: 'Sénégal', team: 'Team_ZH', agent: '' } : {})
  });

  const safeRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.rows)) return payload.rows;
    return [];
  };

  const toNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const normalized = String(value ?? '').replace(/\s/g, '').replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatMoney = (value, currency) => {
    const amount = toNumber(value);
    return `${amount.toLocaleString('fr-CH', { maximumFractionDigits: 2 })} ${currency}`;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const scalar = readScalar(value);
    if (!scalar) return '-';
    const date = new Date(scalar);
    if (Number.isNaN(date.getTime())) return scalar.slice(0, 10);
    return date.toLocaleDateString(language === 'EN' ? 'en-GB' : language === 'DE' ? 'de-CH' : 'fr-CH');
  };

  const normalizeSupplier = (value) => {
    const name = readScalar(value);
    if (!name || ['-', 'N/A', 'NA', 'A CONFIRMER', 'INCONNU'].includes(normalizeLookupKey(name))) return '';
    return name;
  };

  const joinSet = (set) => Array.from(set).filter(Boolean).join(', ') || '-';

  const buildSuppliersFromSources = (expenseRows, inventoryRows, directory = agentsByTeam) => {
    const suppliersByKey = new Map();

    const ensureSupplier = (name) => {
      const normalizedName = normalizeSupplier(name);
      if (!normalizedName) return null;
      const key = normalizeLookupKey(normalizedName);
      if (!suppliersByKey.has(key)) {
        suppliersByKey.set(key, {
          id: `FOU-${String(suppliersByKey.size + 1).padStart(3, '0')}`,
          nom: normalizedName,
          sources: new Set(),
          categories: new Set(),
          departements: new Set(),
          teams: new Set(),
          agents: new Set(),
          assignmentIssues: [],
          pays: new Set(),
          refs: new Set(),
          lignesDepenses: 0,
          lignesStocks: 0,
          montantChf: 0,
          montantCfa: 0,
          lastDate: ''
        });
      }
      return suppliersByKey.get(key);
    };

    const touchDate = (supplier, value) => {
      if (!value) return;
      const next = readScalar(value).slice(0, 10);
      if (!next) return;
      if (!supplier.lastDate || next > supplier.lastDate) supplier.lastDate = next;
    };

    const addValues = (set, value, normalizer = readScalar) => {
      splitSourceValues(value).forEach(item => {
        const normalized = normalizer(item);
        if (normalized && normalized !== '-') set.add(normalized);
      });
    };

    const addAssignment = (supplier, row) => {
      const rawTeams = splitSourceValues(row.team || row.equipe);
      const teams = rawTeams.map(normalizeTeam).filter(team => teamOptions.some(option => option.value === team));
      teams.forEach(team => supplier.teams.add(team));

      splitSourceValues(row.agent || row.responsable || row.owner).forEach(rawAgent => {
        const agent = normalizeAgent(rawAgent);
        const governedTeam = getAgentTeam(agent, directory);
        if (governedTeam && teams.length && !teams.includes(governedTeam)) {
          supplier.assignmentIssues.push({ team: teams.join(', '), agent });
          return;
        }
        if (agent) supplier.agents.add(agent);
      });
    };

    expenseRows.forEach((row) => {
      const supplier = ensureSupplier(row.fournisseur || row.supplier || row.vendor);
      if (!supplier) return;
      supplier.sources.add('Dépenses');
      supplier.lignesDepenses += 1;
      supplier.montantChf += toNumber(row.montant_chf ?? row.amount_chf ?? row.chf);
      supplier.montantCfa += toNumber(row.montant_cfa ?? row.amount_cfa ?? row.cfa);
      addValues(supplier.categories, row.category || row.categorie || row.nature);
      addValues(supplier.departements, row.departement || row.department);
      addAssignment(supplier, row);
      addValues(supplier.pays, row.pays || row.country, normalizeCountry);
      addValues(supplier.refs, row.ref || row.reference || row.id);
      touchDate(supplier, row.date_created || row.date || row.date_operation);
    });

    inventoryRows.forEach((row) => {
      const supplier = ensureSupplier(row.fournisseur || row.supplier || row.vendor);
      if (!supplier) return;
      supplier.sources.add('Stocks & Actifs');
      supplier.lignesStocks += 1;
      supplier.montantChf += toNumber(row.valeur_chf ?? row.achat_chf ?? row.montant_chf);
      supplier.montantCfa += toNumber(row.valeur_cfa ?? row.achat_cfa ?? row.montant_cfa);
      addValues(supplier.categories, row.categorie || row.category || row.sous_categorie);
      addValues(supplier.departements, row.departement || row.bu || row.department);
      addAssignment(supplier, row);
      addValues(supplier.pays, row.pays || row.localisation || row.country, normalizeCountry);
      addValues(supplier.refs, row.source_id || row.ref || row.reference || row.id);
      touchDate(supplier, row.date_achat || row.date_created || row.date || row.date_operation);
    });

    return Array.from(suppliersByKey.values())
      .map((supplier) => ({
        ...supplier,
        sourcesLabel: joinSet(supplier.sources),
        categorie: joinSet(supplier.categories),
        departement: joinSet(supplier.departements),
        team: joinSet(supplier.teams),
        agent: joinSet(supplier.agents),
        pays: joinSet(supplier.pays),
        assignmentIssueCount: supplier.assignmentIssues.length,
        references: supplier.refs.size
      }))
      .sort((a, b) => b.montantChf - a.montantChf || a.nom.localeCompare(b.nom));
  };

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const activeTab = ['overview', 'architecture', 'processes', 'commandes', 'services', 'fournisseurs', 'stocks', 'manufacturing', 'assistant', 'resources', 'glossary'].includes(requestedTab)
    ? requestedTab : 'overview';
  const showLocalPilot = ['commandes', 'stocks'].includes(activeTab);
  const [commandes, setCommandes] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [fournisseursLoading, setFournisseursLoading] = useState(false);
  const [fournisseursError, setFournisseursError] = useState('');
  const [directoryMembers, setDirectoryMembers] = useState([]);
  const [agentDirectoryStatus, setAgentDirectoryStatus] = useState('loading');
  const [stocks, setStocks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('commande');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(getDefaultFormData('commande'));
  const agentsByTeam = useMemo(() => buildTeamAgentDirectory(directoryMembers, {
    [TEAM_CODES.ZURICH]: t.teamZhCollective,
    [TEAM_CODES.SENEGAL]: t.teamSnCollective
  }), [directoryMembers, t.teamSnCollective, t.teamZhCollective]);

  const selectTab = (tab) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`/production?${params.toString()}`);
  };

  useEffect(() => {
    setCommandes([
      { id: 1, numero: 'CMD-001', client: 'SENELEC', produit: 'Solutions IT', quantite: 10, statut: 'Livrée', date: '2026-04-01' },
      { id: 2, numero: 'CMD-002', client: 'UCAD', produit: 'Formation', quantite: 50, statut: 'En cours', date: '2026-04-05' },
      { id: 3, numero: 'CMD-003', client: 'Orange Sénégal', produit: 'Consultation', quantite: 5, statut: 'En cours', date: '2026-04-10' },
      { id: 4, numero: 'CMD-004', client: 'Banque Sénégalaise', produit: 'Implémentation', quantite: 1, statut: 'Préparation', date: '2026-04-15' },
    ]);
 
    setStocks([
      { id: 1, produit: 'Licences IT', quantite: 250, seuil: 100, unite: 'Unité' },
      { id: 2, produit: 'Matériel Informatique', quantite: 45, seuil: 20, unite: 'Unité' },
      { id: 3, produit: 'Fournitures Bureau', quantite: 500, seuil: 200, unite: 'Unité' },
      { id: 4, produit: 'Consommables', quantite: 80, seuil: 50, unite: 'Unité' },
    ]);
  }, []);

  useEffect(() => {
    if (activeTab !== 'fournisseurs') return undefined;
    let cancelled = false;

    const loadFournisseurs = async () => {
      setFournisseursLoading(true);
      setFournisseursError('');
      try {
        const [expensesResult, inventoryResult, directoryResult] = await Promise.allSettled([
          api.getExpenses(500, 0),
          api.getInventory(500, 0),
          api.getMembersDirectory(100, 0)
        ]);
        if (cancelled) return;
        if (expensesResult.status === 'rejected') throw expensesResult.reason;
        if (inventoryResult.status === 'rejected') throw inventoryResult.reason;

        const members = directoryResult.status === 'fulfilled' ? safeRows(directoryResult.value) : [];
        const nextDirectory = buildTeamAgentDirectory(members, {
          [TEAM_CODES.ZURICH]: t.teamZhCollective,
          [TEAM_CODES.SENEGAL]: t.teamSnCollective
        });
        setDirectoryMembers(members);
        setAgentDirectoryStatus(directoryResult.status === 'fulfilled' ? 'available' : 'unavailable');
        setFournisseurs(buildSuppliersFromSources(
          safeRows(expensesResult.value),
          safeRows(inventoryResult.value),
          nextDirectory
        ));
      } catch (error) {
        if (cancelled) return;
        setFournisseurs([]);
        setFournisseursError(error.message || 'Erreur fournisseurs');
        setAgentDirectoryStatus('unavailable');
      } finally {
        if (!cancelled) setFournisseursLoading(false);
      }
    };

    loadFournisseurs();

    return () => {
      cancelled = true;
    };
    // buildSuppliersFromSources is pure and intentionally scoped to this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const totalCommandes = commandes.length;
  const commandesLivrees = commandes.filter(c => normalizeStatus(c.statut) === 'Livrée').length;
  const stocksBas = stocks.filter(s => s.quantite <= s.seuil).length;
 
 
  const handleFormChange = (field, value) => {
    if (field === 'team') {
      const normalizedTeam = normalizeTeam(value);
      setFormData(prev => ({
        ...prev,
        team: normalizedTeam,
        agent: ''
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };
 
  const handleSave = () => {
    if (modalType === 'commande' && (!formData.numero || !formData.client || !formData.produit || !formData.quantite)) {
      alert(t.remplirChamps);
      return;
    }
    if (modalType === 'fournisseur' && (!formData.nom || (!editingId && !formData.email))) {
      alert(t.remplirChamps);
      return;
    }
    if (modalType === 'stock' && (!formData.produit || !formData.quantite || !formData.seuil)) {
      alert(t.remplirChamps);
      return;
    }
 
    const normalizedData = {
      ...formData,
      statut: normalizeStatus(formData.statut)
    };

    if (modalType === 'commande') {
      if (editingId) {
        setCommandes(commandes.map(c => c.id === editingId ? { ...normalizedData, id: editingId } : c));
      } else {
        setCommandes([...commandes, { ...normalizedData, id: Date.now() }]);
      }
    } else if (modalType === 'fournisseur') {
      const fournisseurData = {
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        categorie: formData.categorie,
        departement: formData.departement,
        pays: normalizeCountry(formData.pays),
        team: normalizeTeam(formData.team),
        agent: formData.agent
      };
      if (editingId) {
        setFournisseurs(fournisseurs.map(f => f.id === editingId
          ? { ...f, ...fournisseurData, id: editingId, assignmentIssueCount: 0, assignmentIssues: [] }
          : f));
      } else {
        setFournisseurs([...fournisseurs, { ...fournisseurData, id: Date.now() }]);
      }
    } else {
      const stockData = {
        produit: formData.produit,
        quantite: parseInt(formData.quantite, 10) || 0,
        seuil: parseInt(formData.seuil, 10) || 0,
        unite: formData.unite
      };
      if (editingId) {
        setStocks(stocks.map(s => s.id === editingId ? { ...stockData, id: editingId } : s));
      } else {
        setStocks([...stocks, { ...stockData, id: Date.now() }]);
      }
    }
 
    setShowModal(false);
    setEditingId(null);
    setFormData(getDefaultFormData(modalType));
  };
 
  const handleEdit = (type, item) => {
    setModalType(type);
    setEditingId(item.id);
    const editableTeam = type === 'fournisseur' ? resolveEditableTeam(item.team) : '';
    const editableItem = type === 'fournisseur'
      ? {
          ...item,
          team: editableTeam,
          agent: item.assignmentIssueCount ? '' : resolveEditableAgent(editableTeam, item.agent)
        }
      : item;
    setFormData({ ...getDefaultFormData(type), ...editableItem });
    setShowModal(true);
  };

  const handleDeleteSupplier = (item) => {
    if (!item || !window.confirm(t.confirmerSuppressionFournisseur)) return;
    setFournisseurs(current => current.filter(fournisseur => fournisseur.id !== item.id));
    setShowModal(false);
    setEditingId(null);
  };

  const editingSupplier = modalType === 'fournisseur' && editingId
    ? fournisseurs.find(fournisseur => fournisseur.id === editingId)
    : null;

  const closeFormModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(getDefaultFormData(modalType));
  };
 
  return (
    <>
      <div className="m3s-business-module min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-8">
        <div className="mx-auto w-full max-w-[1800px]">

        <div id="production-module-tabs" className="scroll-mt-24">
          <ModulePageTabs
            moduleId="production"
            language={language}
            activeTab={activeTab}
            onSelect={selectTab}
            tabs={[
              { tab: 'overview', label: t.overview },
              { tab: 'commandes', label: t.commandes },
              { tab: 'fournisseurs', label: t.fournisseurs },
              { tab: 'stocks', label: t.stocks },
              { tab: 'glossary', label: t.glossary }
            ]}
          />
        </div>

        <div className={`production-content-stack${activeTab === 'overview' ? '' : ' production-content-stack--detail'}`}>
        {showLocalPilot && <section className="production-pilot-summary mb-6 rounded-lg border border-blue-500/40 bg-blue-950/45 p-5 text-blue-50">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="mt-0.5 shrink-0 text-blue-300" aria-hidden="true" />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-bold text-white">{t.pilotTitle}</h2>
                  <span className="rounded border border-blue-400/50 bg-blue-500/15 px-2 py-1 text-xs font-semibold text-blue-100">
                    {t.pilotScope}
                  </span>
                </div>
                <p className="mt-2 max-w-4xl text-sm leading-6 text-blue-100">{t.pilotDescription}</p>
                <p className="mt-1 text-sm text-blue-200">{t.connectedSuppliers}</p>
              </div>
            </div>
            <dl className="grid shrink-0 gap-2 text-xs text-blue-100 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded border border-blue-400/25 bg-slate-950/25 px-3 py-2">{t.pilotSource}</div>
              <div className="rounded border border-blue-400/25 bg-slate-950/25 px-3 py-2">{t.pilotFreshness}</div>
            </dl>
          </div>
        </section>}
 
        {/* KPIs */}
        {showLocalPilot && <div className="production-kpi-summary grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg p-6 border border-blue-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm">{t.totalCommandes}</p>
                <p className="text-white text-2xl font-bold">{totalCommandes}</p>
              </div>
              <Package size={32} className="text-blue-400" />
            </div>
          </div>
 
          <div className="bg-gradient-to-br from-green-900 to-green-800 rounded-lg p-6 border border-green-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-sm">{t.commandesLivrees}</p>
                <p className="text-white text-2xl font-bold">{commandesLivrees}</p>
              </div>
              <CheckCircle size={32} className="text-green-400" />
            </div>
          </div>
 
 
          <div className={`bg-gradient-to-br ${stocksBas === 0 ? 'from-green-900 to-green-800' : 'from-orange-900 to-orange-800'} rounded-lg p-6 border ${stocksBas === 0 ? 'border-green-700' : 'border-orange-700'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${stocksBas === 0 ? 'text-green-200' : 'text-orange-200'} text-sm`}>{t.alertStocks}</p>
                <p className="text-white text-2xl font-bold">{stocksBas}</p>
              </div>
              <AlertCircle size={32} className={stocksBas === 0 ? 'text-green-400' : 'text-orange-400'} />
            </div>
          </div>
        </div>}
 
        <ModuleTabContent activeTab={activeTab} className="production-active-view">
        {activeTab === 'services' && <ProductionServices language={language} />}
        {/* Vue d'ensemble */}
        {activeTab === 'overview' && <ProductionOverview language={language} onOpenServices={() => selectTab('services')} />}
 
        {/* Tables */}
        {activeTab === 'commandes' && (
          <div id="production-orders-register" className="scroll-mt-24" tabIndex="-1">
            <div className="mb-4 rounded-lg border border-slate-600 bg-slate-800/70 px-4 py-3 text-sm text-slate-200">
              {t.noWriteAction}
            </div>
            <TableControls rows={commandes} renderTable={(visibleRows) => (
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.numero}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.client}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.produit}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.quantite}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.statut}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(c => (
                    <tr key={c.id} className="border-t border-slate-700 hover:bg-slate-700/50">
                      <td className="px-4 py-2 text-slate-300 font-medium">{c.numero}</td>
                      <td className="px-4 py-2 text-slate-400">{c.client}</td>
                      <td className="px-4 py-2 text-slate-400">{translateProduct(c.produit)}</td>
                      <td className="px-4 py-2 text-slate-400">{c.quantite}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusClass(c.statut)}`}>
                          {translateStatus(c.statut)}
                        </span>
                      </td>
                      <td className="px-4 py-2 flex gap-2">
                        <button type="button" disabled aria-label={t.modifier} className="cursor-not-allowed p-1 opacity-35">
                          <Edit2 size={16} className="text-blue-400" />
                        </button>
                        <button type="button" disabled aria-label={t.supprimer} className="cursor-not-allowed p-1 opacity-35">
                          <Trash2 size={16} className="text-red-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )} />
          </div>
        )}

        {activeTab === 'architecture' && <FunctionArchitectureOverview moduleId="production" language={language} />}

        {activeTab === 'processes' && <FunctionProcessOverview moduleId="production" language={language} />}

        {activeTab === 'manufacturing' && (
          <section className="text-slate-100">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-md border border-orange-400/30 bg-orange-500/10 p-2 text-orange-300">
                  <Wrench size={22} aria-hidden="true" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{t.manufacturingTitle}</h3>
                    <span className="rounded border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-xs font-semibold text-amber-200">
                      {t.manufacturingStatus}
                    </span>
                  </div>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">{t.manufacturingDescription}</p>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">{t.manufacturingGuardrail}</p>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {t.manufacturingSteps.map((step, index) => (
                <div key={step} className="flex min-h-20 items-center gap-3 rounded-md border border-slate-600 bg-slate-900/45 px-4 py-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-400/40 bg-blue-500/10 text-sm font-bold text-blue-200">
                    {index + 1}
                  </span>
                  <span className="text-sm font-semibold text-slate-100">{step}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm leading-6 text-slate-300">{t.manufacturingNext}</p>
            <p className="mt-3 text-xs leading-5 text-slate-400">{t.manufacturingSource}</p>
            <ProductionManufacturingKit language={language} />
            <ProductionTools language={language} />
            <button type="button" className="m3s-secondary-button mt-4 min-h-11 px-3" onClick={() => selectTab('services')}>{t.openServices}</button>
          </section>
        )}

        {activeTab === 'fournisseurs' && (
          <div id="production-suppliers-register" className="scroll-mt-24" tabIndex="-1">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-slate-300">{t.registreFournisseurs}</p>
              <StandardCreateButton onClick={() => { setEditingId(null); setModalType('fournisseur'); setFormData(getDefaultFormData('fournisseur')); setShowModal(true); }}>
                {t.preparationFournisseur}
              </StandardCreateButton>
            </div>
            {fournisseursError && (
              <div className="mb-4 rounded-lg border border-red-700 bg-red-950/40 px-4 py-3 text-sm text-red-200">
                {fournisseursError}
              </div>
            )}
            <TableControls rows={fournisseurs} renderTable={(visibleRows) => (
              <table className="min-w-[1450px] text-sm">
                <thead className="sticky top-0 z-10 bg-slate-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.reference}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.nom}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.sources}</th>
                    <th className="px-4 py-2 text-right text-white font-bold">{t.montantChf}</th>
                    <th className="px-4 py-2 text-right text-white font-bold">{t.montantCfa}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.lignesDepenses}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.lignesStocks}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.categorie}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.departement}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.team}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.agent}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.pays}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.derniereOperation}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {fournisseursLoading && (
                    <tr>
                      <td colSpan="14" className="px-4 py-6 text-center text-slate-300">Chargement...</td>
                    </tr>
                  )}
                  {!fournisseursLoading && visibleRows.length === 0 && (
                    <tr>
                      <td colSpan="14" className="px-4 py-6 text-center text-slate-400">{t.aucuneDonnee}</td>
                    </tr>
                  )}
                  {visibleRows.map(f => (
                    <tr key={f.id} onClick={() => handleEdit('fournisseur', f)} className="cursor-pointer border-t border-slate-700 hover:bg-slate-700/50">
                      <td className="px-4 py-2 text-slate-400">{f.id}</td>
                      <td className="px-4 py-2 text-slate-300 font-medium">{f.nom}</td>
                      <td className="px-4 py-2 text-slate-400">{translateJoinedValues(f.sourcesLabel, translateSource)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-emerald-300">{formatMoney(f.montantChf, 'CHF')}</td>
                      <td className="px-4 py-2 text-right font-semibold text-amber-300">{formatMoney(f.montantCfa, 'CFA')}</td>
                      <td className="px-4 py-2 text-slate-400">{f.lignesDepenses}</td>
                      <td className="px-4 py-2 text-slate-400">{f.lignesStocks}</td>
                      <td className="px-4 py-2 text-slate-400">{translateJoinedValues(f.categorie, translateCategory)}</td>
                      <td className="px-4 py-2 text-slate-400">{translateJoinedValues(f.departement, translateDepartment)}</td>
                      <td className="px-4 py-2 text-slate-400">{translateJoinedValues(f.team, translateTeam)}</td>
                      <td className={`px-4 py-2 ${f.assignmentIssueCount ? 'font-semibold text-amber-300' : 'text-slate-400'}`}>{supplierAgentLabel(f)}</td>
                      <td className="px-4 py-2 text-slate-400">{translateJoinedValues(f.pays, translateCountry)}</td>
                      <td className="px-4 py-2 text-slate-400">{formatDate(f.lastDate)}</td>
                      <StandardActionsCell
                        item={f}
                        onEdit={(item) => handleEdit('fournisseur', item)}
                        onDelete={handleDeleteSupplier}
                        labels={{ view: t.voir, edit: t.modifier, delete: t.supprimer }}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            )} />
          </div>
        )}
 
        {activeTab === 'stocks' && (
          <div>
            <div className="mb-4 rounded-lg border border-slate-600 bg-slate-800/70 px-4 py-3 text-sm text-slate-200">
              {t.noWriteAction}
            </div>
            <TableControls rows={stocks} renderTable={(visibleRows) => (
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.produit}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.quantite}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.seuil}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.statut}</th>
                    <th className="px-4 py-2 text-left text-white font-bold">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(s => (
                    <tr key={s.id} className="border-t border-slate-700 hover:bg-slate-700/50">
                      <td className="px-4 py-2 text-slate-300 font-medium">{translateProduct(s.produit)}</td>
                      <td className="px-4 py-2 text-slate-400">{s.quantite}</td>
                      <td className="px-4 py-2 text-slate-400">{s.seuil}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${s.quantite > s.seuil ? 'bg-green-900 text-green-200' : 'bg-orange-900 text-orange-200'}`}>
                          {s.quantite > s.seuil ? t.ok : t.bas}
                        </span>
                      </td>
                      <td className="px-4 py-2 flex gap-2">
                        <button type="button" disabled aria-label={t.modifier} className="cursor-not-allowed p-1 opacity-35">
                          <Edit2 size={16} className="text-blue-400" />
                        </button>
                        <button type="button" disabled aria-label={t.supprimer} className="cursor-not-allowed p-1 opacity-35">
                          <Trash2 size={16} className="text-red-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )} />
          </div>
        )}

        {activeTab === 'resources' && <FunctionResourcesOverview moduleId="production" language={language} onSelectTab={selectTab} />}

        {activeTab === 'assistant' && <FunctionAssistant moduleId="production" language={language} />}

        {activeTab === 'glossary' && <ProductionGlossary language={language} />}

        <ChildTabPlaceholder moduleId="production" language={language} activeTab={activeTab} handledTabs={['overview', 'architecture', 'processes', 'commandes', 'services', 'manufacturing', 'fournisseurs', 'stocks', 'assistant', 'resources', 'glossary']} />
        </ModuleTabContent>
        </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="production-record-modal-title" className={`max-h-[90vh] w-full overflow-y-auto rounded-lg border border-slate-700 bg-slate-800 p-5 shadow-2xl sm:p-8 ${modalType === 'fournisseur' ? 'max-w-4xl' : 'max-w-md'}`}>
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                {modalType === 'fournisseur' && <p className="text-sm font-semibold uppercase text-blue-300">{t.ficheFournisseur}</p>}
                <h2 id="production-record-modal-title" className="mt-1 text-2xl font-semibold text-white">
                  {editingId && modalType === 'fournisseur'
                    ? formData.nom || t.modifierFournisseur
                    : modalType === 'commande'
                      ? t.nouvelleCommande
                      : modalType === 'fournisseur'
                        ? t.nouveauFournisseur
                        : t.ajouterStock}
                </h2>
                {modalType === 'fournisseur' && <p className="mt-2 text-sm text-amber-200">{t.registreFournisseurs}</p>}
              </div>
              <button type="button" onClick={closeFormModal} className="m3s-icon-button shrink-0 text-slate-300 hover:bg-slate-700 hover:text-white" aria-label={t.annuler} title={t.annuler}>
                <X size={20} />
              </button>
            </div>

            {editingSupplier && (
              <section className="mb-6">
                <h3 className="mb-3 text-sm font-semibold uppercase text-slate-300">{t.donneesSourceLectureSeule}</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {[
                    [t.reference, editingSupplier.id],
                    [t.sources, translateJoinedValues(editingSupplier.sourcesLabel, translateSource)],
                    [t.montantChf, formatMoney(editingSupplier.montantChf, 'CHF')],
                    [t.montantCfa, formatMoney(editingSupplier.montantCfa, 'CFA')],
                    [t.lignesDepenses, editingSupplier.lignesDepenses],
                    [t.lignesStocks, editingSupplier.lignesStocks],
                    [t.derniereOperation, formatDate(editingSupplier.lastDate)],
                    [t.nbReferences, editingSupplier.references]
                  ].map(([label, value]) => (
                    <div key={label} className="m3s-raised px-4 py-3">
                      <p className="text-xs uppercase text-slate-500">{label}</p>
                      <p className="mt-1 break-words text-sm font-semibold text-slate-100">{value ?? '-'}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="space-y-4">
              {modalType === 'commande' && (
                <>
                  <input type="text" placeholder={t.numero} value={formData.numero} onChange={(e) => handleFormChange('numero', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                  <input type="text" placeholder={t.client} value={formData.client} onChange={(e) => handleFormChange('client', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                  <input type="text" placeholder={t.produit} value={formData.produit} onChange={(e) => handleFormChange('produit', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                  <input type="number" placeholder={t.quantite} value={formData.quantite} onChange={(e) => handleFormChange('quantite', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                  <select value={formData.statut} onChange={(e) => handleFormChange('statut', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500">
                    <option value="En cours">{translateStatus('En cours')}</option>
                    <option value="Préparation">{translateStatus('Préparation')}</option>
                    <option value="Livrée">{translateStatus('Livrée')}</option>
                  </select>
                  <LocalizedDateInput value={formData.date} onChange={(date) => handleFormChange('date', date)} className="w-full" />
                </>
              )}

              {modalType === 'fournisseur' && (
                <section>
                  <h3 className="mb-3 text-sm font-semibold uppercase text-slate-300">{t.champsModifiables}</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label>
                      <span className="m3s-field-label">{t.nomFournisseur} *</span>
                      <input type="text" value={formData.nom} onChange={(e) => handleFormChange('nom', e.target.value)} className="m3s-field mt-1 w-full" />
                    </label>
                    <label>
                      <span className="m3s-field-label">{t.email}{editingId ? '' : ' *'}</span>
                      <input type="email" value={formData.email} onChange={(e) => handleFormChange('email', e.target.value)} className="m3s-field mt-1 w-full" />
                    </label>
                    <label>
                      <span className="m3s-field-label">{t.telephone}</span>
                      <input type="tel" value={formData.telephone} onChange={(e) => handleFormChange('telephone', e.target.value)} className="m3s-field mt-1 w-full" />
                    </label>
                    <label>
                      <span className="m3s-field-label">{t.categorie}</span>
                      <select aria-label={t.categorie} value={formData.categorie} onChange={(e) => handleFormChange('categorie', e.target.value)} className="m3s-field mt-1 w-full">
                        {formData.categorie && !supplierCategoryOptions.includes(formData.categorie) && (
                          <option value={formData.categorie}>{translateJoinedValues(formData.categorie, translateCategory)} ({t.agentHistorique})</option>
                        )}
                        {supplierCategoryOptions.map(category => (
                          <option key={category} value={category}>{translateCategory(category)}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="m3s-field-label">{t.departement}</span>
                      <select aria-label={t.departement} value={formData.departement || ''} onChange={(e) => handleFormChange('departement', e.target.value)} className="m3s-field mt-1 w-full">
                        <option value="">{t.nonRenseigne}</option>
                        {formData.departement && !supplierDepartmentOptions.includes(formData.departement) && (
                          <option value={formData.departement}>{translateJoinedValues(formData.departement, translateDepartment)} ({t.agentHistorique})</option>
                        )}
                        {supplierDepartmentOptions.map(departement => (
                          <option key={departement} value={departement}>{translateDepartment(departement)}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="m3s-field-label">{t.team}</span>
                      <select aria-label={t.team} value={resolveEditableTeam(formData.team)} onChange={(e) => handleFormChange('team', e.target.value)} className="m3s-field mt-1 w-full">
                      {teamOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                      </select>
                    </label>
                    <label>
                      <span className="m3s-field-label">{t.agent}</span>
                      <select aria-label={t.agent} value={formData.agent} onChange={(e) => handleFormChange('agent', e.target.value)} className="m3s-field mt-1 w-full">
                      <option value="">{t.agentNonAttribue}</option>
                      {formData.agent && !(agentsByTeam[resolveEditableTeam(formData.team)] || []).some(option => option.value === formData.agent) && (
                        <option value={formData.agent}>{formData.agent} ({t.agentHistorique})</option>
                      )}
                      {(agentsByTeam[resolveEditableTeam(formData.team)] || []).map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                      </select>
                    </label>
                    <p className={`text-xs leading-5 md:col-span-2 ${agentDirectoryStatus === 'available' ? 'text-emerald-300' : 'text-amber-300'}`} role="status">
                      {agentDirectoryStatus === 'loading'
                        ? t.agentSourceLoading
                        : agentDirectoryStatus === 'available'
                          ? t.agentSourceAvailable
                          : t.agentSourceUnavailable}
                    </p>
                    <label>
                      <span className="m3s-field-label">{t.pays}</span>
                      <select aria-label={t.pays} value={formData.pays} onChange={(e) => handleFormChange('pays', e.target.value)} className="m3s-field mt-1 w-full">
                        {formData.pays && !['Sénégal', 'France', 'Suisse'].includes(formData.pays) && (
                          <option value={formData.pays}>{translateJoinedValues(formData.pays, translateCountry)} ({t.agentHistorique})</option>
                        )}
                        <option value="Sénégal">{translateCountry('Sénégal')}</option>
                        <option value="France">{translateCountry('France')}</option>
                        <option value="Suisse">{translateCountry('Suisse')}</option>
                      </select>
                    </label>
                    <div className={`rounded-md border px-4 py-3 md:col-span-2 ${editingSupplier?.assignmentIssueCount && !formData.agent ? 'border-amber-500/60 bg-amber-950/25 text-amber-200' : 'border-emerald-500/40 bg-emerald-950/20 text-emerald-200'}`}>
                      <span className="text-xs font-semibold uppercase">{t.coherenceAffectation}</span>
                      <p className="mt-1 text-sm font-semibold">{editingSupplier?.assignmentIssueCount && !formData.agent ? t.affectationIncoherente : t.affectationCoherente}</p>
                    </div>
                  </div>
                </section>
              )}

              {modalType === 'stock' && (
                <>
                  <input type="text" placeholder={t.produit} value={formData.produit} onChange={(e) => handleFormChange('produit', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                  <input type="number" placeholder={t.quantite} value={formData.quantite} onChange={(e) => handleFormChange('quantite', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                  <input type="number" placeholder={t.seuil} value={formData.seuil} onChange={(e) => handleFormChange('seuil', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                  <input type="text" placeholder={t.unite} value={formData.unite} onChange={(e) => handleFormChange('unite', e.target.value)} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500" />
                </>
              )}
            </div>
 
            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-700 pt-4 sm:flex-row sm:justify-end">
              {editingSupplier && (
                <button type="button" onClick={() => handleDeleteSupplier(editingSupplier)} className="m3s-danger-button min-h-11 px-4 sm:mr-auto">
                  <Trash2 size={17} /> {t.supprimer}
                </button>
              )}
              <button type="button" onClick={closeFormModal} className="m3s-secondary-button min-h-11 px-4">{t.annuler}</button>
              <button type="button" onClick={handleSave} className={`${editingId ? 'm3s-primary-button' : 'm3s-success-button'} min-h-11 px-4`}>
                {editingId ? <Edit2 size={17} /> : null} {editingId ? t.modifier : t.creer}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Production;
